import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import RefreshButton from '../components/RefreshButton';
import { Brain, Activity } from 'lucide-react';

const BARS = [
  { key: 'trend', label: 'Trend' },
  { key: 'momentum', label: 'Momentum' },
  { key: 'volatility', label: 'Volatility' },
  { key: 'volume', label: 'Volume' },
  { key: 'bollinger', label: 'Bollinger' },
];

export default function Learning() {
  const [data, setData] = useState(null);
  const [last, setLast] = useState(null);
  const [mem, setMem] = useState(null);      // P11 regime memory
  const [strat, setStrat] = useState(null);  // P11 strategy memory
  const [guard, setGuard] = useState(null);  // P11 failure guard
  const [calib, setCalib] = useState(null);  // P11 calibration table
  const [truth, setTruth] = useState(null);  // 50.J — canonical source
  const [perf, setPerf] = useState(null);    // 50.H — strategy performance
  const [forens, setForens] = useState(null); // 50-summary aggregate

  const load = async () => {
    try {
      const { data } = await api.get('/learning/stats');
      setData(data);
    } catch { /* non-fatal */ }
    setLast(new Date().toISOString());
    // Phase 50.J — use Promise.allSettled so a single endpoint failure
    // (e.g. legacy /intelligence/strategy-memory occasionally 500s on
    // mixed-schema rows) doesn't nullify ALL panel state.
    const results = await Promise.allSettled([
      api.get('/intelligence/regime-memory'),
      api.get('/intelligence/strategy-memory'),
      api.get('/intelligence/failure-guard'),
      api.get('/intelligence/calibration'),
      api.get('/truth/source?fresh=true'),    // 50.J
      api.get('/strategy/performance'),       // 50.H
      api.get('/forensics/summary'),          // batch summary
    ]);
    const [m, s, g, c, t, p, f] = results;
    if (m.status === 'fulfilled') setMem(m.value.data?.regimes || {});
    if (s.status === 'fulfilled') setStrat(s.value.data?.regimes || {});
    if (g.status === 'fulfilled') setGuard(g.value.data || null);
    if (c.status === 'fulfilled') setCalib(c.value.data || null);
    if (t.status === 'fulfilled') setTruth(t.value.data || null);
    if (p.status === 'fulfilled') setPerf(p.value.data || null);
    if (f.status === 'fulfilled') setForens(f.value.data || null);
  };
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, []);

  const w = data?.weights || {};
  const d = data?.defaults || {};
  const g = data?.global || {};
  const rh = data?.regime_health || {};
  const alerts = rh.shift_alerts || [];

  const pct = (v) => `${Math.round((v / 1.5) * 100)}%`;

  const statusColor = (s) => s === 'degraded' ? 'text-sell' : s === 'watch' ? 'text-hold' : s === 'healthy' ? 'text-buy' : 'text-[var(--txt-secondary)]';

  // Phase 50.J — canonical numbers come from /api/truth/source.
  // Legacy g.* values were independently computed and could disagree.
  const truthOv = truth?.overall || {};
  const trueTotal = truthOv.n ?? g.total_trades ?? 0;
  const trueWinRate = truthOv.win_rate_pct ?? g.win_rate ?? 0;
  const trueNetPnl = truthOv.total_pnl ?? g.net_pnl ?? 0;
  const truthChecksum = truth?.checksum || '—';
  const validityPct = truth?.attribution?.validity_pct ?? 0;
  const stratState = perf?.overall?.state || '—';
  const stratScore = perf?.overall?.strategy_health_score ?? 0;
  const stratColor = stratState === 'HEALTHY' ? 'text-buy'
                    : stratState === 'WATCH' ? 'text-cyan-sys'
                    : stratState === 'DEGRADED' ? 'text-hold' : 'text-sell';

  return (
    <div className="p-4 space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--txt-muted)] font-mono"
             data-testid="learning-truth-checksum">
          single-source · truth/checksum: {truthChecksum} · validity: {validityPct}%
        </div>
        <RefreshButton onRefresh={load} lastUpdated={last} testId="learning-refresh" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="panel p-3" data-testid="learning-total-trades">
          <div className="text-[10px] tracking-[0.3em] uppercase font-display text-cyan-sys">Total Trades</div>
          <div className="font-mono text-2xl mt-1">{trueTotal}</div>
          <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] mt-1">truth/source · 90d</div>
        </div>
        <div className="panel p-3" data-testid="learning-win-rate">
          <div className="text-[10px] tracking-[0.3em] uppercase font-display text-cyan-sys">Win Rate</div>
          <div className={`font-mono text-2xl mt-1 ${trueWinRate >= 45 ? 'text-buy' : trueWinRate >= 30 ? 'text-hold' : 'text-sell'}`}>{trueWinRate}%</div>
          <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] mt-1">canonical · breakeven incl.</div>
        </div>
        <div className="panel p-3" data-testid="learning-net-pnl">
          <div className="text-[10px] tracking-[0.3em] uppercase font-display text-cyan-sys">Net P&L</div>
          <div className={`font-mono text-2xl mt-1 ${trueNetPnl > 0 ? 'text-buy' : trueNetPnl < 0 ? 'text-sell' : ''}`}>${trueNetPnl}</div>
          <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] mt-1">truth/source · 90d</div>
        </div>
        <div className="panel p-3" data-testid="learning-strategy-health">
          <div className="text-[10px] tracking-[0.3em] uppercase font-display text-cyan-sys">Strategy Health</div>
          <div className={`font-mono text-2xl mt-1 ${stratColor}`}>{stratScore}</div>
          <div className={`text-[9px] uppercase tracking-widest mt-1 ${stratColor}`}>{stratState}</div>
        </div>
      </div>

      {/* Phase 50.J — Strategy Forensics strip on the Learning tab */}
      {forens && (
        <div className="panel p-3" data-testid="learning-forensics-strip">
          <div className="text-[10px] tracking-[0.3em] uppercase font-display text-cyan-sys mb-2">Strategy Forensics</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 font-mono text-[11px]">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">Attribution</div>
              <div className={validityPct >= 95 ? 'text-buy' : validityPct >= 80 ? 'text-hold' : 'text-sell'}>
                {validityPct}%
              </div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">Regime Accuracy</div>
              <div className={forens.regime_classifier?.state === 'ACCURATE' ? 'text-buy'
                              : forens.regime_classifier?.state === 'NOISY' ? 'text-hold' : 'text-sell'}>
                {forens.regime_classifier?.regime_accuracy_pct ?? 0}%
              </div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">Stop Efficiency</div>
              <div>{forens.stop_loss?.stop_efficiency_pct ?? '—'}%</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">Target Quality</div>
              <div className={(forens.target_quality?.target_quality_score ?? 0) >= 60 ? 'text-buy'
                              : (forens.target_quality?.target_quality_score ?? 0) >= 30 ? 'text-hold' : 'text-sell'}>
                {forens.target_quality?.target_quality_score ?? 0}
              </div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">Late Entry Rate</div>
              <div className={(forens.entry_timing?.late_rate_pct ?? 0) < 20 ? 'text-buy'
                              : (forens.entry_timing?.late_rate_pct ?? 0) < 60 ? 'text-hold' : 'text-sell'}>
                {forens.entry_timing?.late_rate_pct ?? 0}%
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="panel p-3" data-testid="activation-panel">
          <div className="text-[10px] tracking-[0.3em] uppercase font-display text-cyan-sys mb-2">Activation Status</div>
          <div className="text-[11px] font-mono text-[var(--txt-secondary)] mb-2">
            Learning freezes per regime until ≥ {g.min_trades_per_regime ?? 20} trades recorded.
          </div>
          {Object.entries(rh.by_trend || {}).length === 0 && <div className="text-[var(--txt-muted)] text-[12px]">// no regime data yet</div>}
          {Object.entries(rh.by_trend || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between py-1 border-b border-[var(--border-default)] text-[12px] font-mono">
              <span>{k}</span>
              <span>
                <span className="text-[var(--txt-secondary)]">{v.trades}/{g.min_trades_per_regime}</span>{' '}
                <span className={v.activated ? 'text-buy' : 'text-hold'}>
                  {v.activated ? 'ACTIVE' : 'FROZEN'}
                </span>
              </span>
            </div>
          ))}
        </div>
        <div className="panel p-3" data-testid="events-panel">
          <div className="text-[10px] tracking-[0.3em] uppercase font-display text-cyan-sys mb-2">Recent Learning Events</div>
          {(data?.events || []).length === 0 && <div className="text-[var(--txt-muted)] text-[12px]">// no learning events yet</div>}
          {(data?.events || []).slice(0, 8).map((e, i) => (
            <div key={i} className="flex gap-2 py-1 border-b border-[var(--border-default)] text-[11px] font-mono">
              <span className={e.kind === 'rollback' ? 'text-sell' : e.kind === 'frozen' ? 'text-hold' : 'text-cyan-sys'}>
                ▸ {e.kind}
              </span>
              <span className="text-[var(--txt-secondary)] flex-1 truncate">
                {e.reason} {e.regime ? `· ${e.regime}` : ''} {e.trades_in_regime != null ? `· ${e.trades_in_regime}/${e.needed}` : ''}
              </span>
            </div>
          ))}
        </div>
      </div>

      <CornerPanel
        title="QHDIS Weights // adaptive sub-score multipliers"
        right={<Brain size={14} className="text-cyan-sys" />}
        testId="learning-weights"
      >
        <div className="space-y-3 font-mono text-[12px]">
          {BARS.map(({ key, label }) => {
            const cur = w[key] ?? d[key] ?? 0;
            const def = d[key] ?? 0;
            const drift = cur - def;
            const driftPct = def ? Math.round((drift / def) * 100) : 0;
            const color = driftPct > 2 ? 'text-buy' : driftPct < -2 ? 'text-sell' : 'text-[var(--txt-secondary)]';
            return (
              <div key={key} data-testid={`weight-${key}`}>
                <div className="flex justify-between text-[11px] mb-1">
                  <span className="uppercase tracking-[0.25em] text-cyan-sys">{label}</span>
                  <span>
                    <span>{cur.toFixed(3)}</span>{' '}
                    <span className="text-[var(--txt-muted)]">(def {def.toFixed(2)})</span>{' '}
                    <span className={color}>{driftPct >= 0 ? '+' : ''}{driftPct}%</span>
                  </span>
                </div>
                <div className="h-2 bg-[var(--bg-void)] border border-[var(--border-muted)] relative">
                  <div
                    className="h-full bg-cyan-sys"
                    style={{ width: pct(cur) }}
                  />
                  <div
                    className="absolute top-0 h-full w-[1px] bg-hold"
                    style={{ left: pct(def) }}
                    title="default"
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-[10px] text-[var(--txt-secondary)] uppercase tracking-widest">
          // weights drift on every closed trade — win strengthens contributing factors, loss weakens them
        </div>
      </CornerPanel>

      {alerts.length > 0 && (
        <CornerPanel title="Regime Shift Alerts" testId="regime-alerts">
          {alerts.map((a) => (
            <div key={a.regime} className="flex justify-between py-2 border-b border-[var(--border-default)] text-[12px] font-mono">
              <span className="text-sell">▸ {a.regime}</span>
              <span>{a.wins}W / {a.losses}L — {a.win_rate}% WR</span>
            </div>
          ))}
        </CornerPanel>
      )}

      <CornerPanel title="Regime Health" right={<Activity size={14} className="text-cyan-sys" />} testId="regime-health">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 font-mono text-[12px]">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-sys mb-1">By Trend</div>
            {Object.entries(rh.by_trend || {}).length === 0 && <div className="text-[var(--txt-muted)]">// no data yet</div>}
            {Object.entries(rh.by_trend || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between py-1 border-b border-[var(--border-default)]">
                <span>{k}</span>
                <span>
                  <span className="text-[var(--txt-secondary)]">{v.trades} trades · {v.win_rate}% · </span>
                  <span className={statusColor(v.status)}>{v.status}</span>
                </span>
              </div>
            ))}
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-sys mb-1">By Volatility</div>
            {Object.entries(rh.by_volatility || {}).length === 0 && <div className="text-[var(--txt-muted)]">// no data yet</div>}
            {Object.entries(rh.by_volatility || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between py-1 border-b border-[var(--border-default)]">
                <span>{k}</span>
                <span>
                  <span className="text-[var(--txt-secondary)]">{v.trades} trades · {v.win_rate}% · </span>
                  <span className={statusColor(v.status)}>{v.status}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </CornerPanel>

      {/* P11: Failure Guard banner */}
      {guard?.engaged && (
        <div data-testid="failure-guard-banner" className="panel border-sell bg-[rgba(255,0,60,0.06)] px-4 py-2 text-[12px] font-mono text-sell flex items-start gap-3 no-trade-glow">
          <span className="font-display tracking-[0.3em] uppercase text-[11px] shrink-0">FAILURE GUARD</span>
          <div className="flex-1">
            <div className="text-[var(--txt-primary)]">{guard.reason}</div>
            {guard.action && <div className="text-[10px] text-cyan-sys uppercase tracking-wider mt-0.5">action → {guard.action}</div>}
          </div>
        </div>
      )}

      {/* P11: Regime Memory — which indicators win per regime */}
      <CornerPanel title="Regime Memory // best indicators per regime" testId="regime-memory-panel">
        {!mem || Object.keys(mem).length === 0 ? (
          <div className="text-[var(--txt-muted)] text-[12px]">// learning from closed trades — need ≥3 per regime</div>
        ) : (
          <div className="mobile-scroll" data-sticky-first="true">
            <table className="w-full text-[12px] font-mono min-w-[720px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.25em] text-cyan-sys border-b border-[var(--border-default)]">
                  <th className="text-left py-2 px-2">regime</th>
                  <th className="text-left py-2 px-2">n</th>
                  <th className="text-left py-2 px-2">win rate</th>
                  <th className="text-left py-2 px-2">avg confidence</th>
                  <th className="text-left py-2 px-2">best indicators</th>
                  <th className="text-left py-2 px-2">state</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(mem).map(([k, v]) => (
                  <tr key={k} className="border-b border-[var(--border-default)]" data-testid={`regime-memory-${k.replace('/', '-')}`}>
                    <td className="py-2 px-2 text-cyan-sys">{k}</td>
                    <td className="py-2 px-2">{v.sample_size}</td>
                    <td className="py-2 px-2">
                      <span className={v.win_rate >= 50 ? 'text-buy' : v.win_rate >= 35 ? 'text-hold' : 'text-sell'}>
                        {v.win_rate != null ? `${v.win_rate}%` : '—'}
                      </span>
                    </td>
                    <td className="py-2 px-2">{v.avg_confidence ?? '—'}</td>
                    <td className="py-2 px-2 text-[11px]">
                      {(v.best_indicators || []).join(' · ') || '—'}
                    </td>
                    <td className={`py-2 px-2 text-[10px] uppercase tracking-wider ${v.activated ? 'text-buy' : 'text-hold'}`}>
                      {v.activated ? 'activated' : 'learning'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CornerPanel>

      {/* P11: Strategy Memory — recommended confluence thresholds */}
      <CornerPanel title="Strategy Memory // long-term recommended thresholds" testId="strategy-memory-panel">
        {!strat || Object.keys(strat).length === 0 ? (
          <div className="text-[var(--txt-muted)] text-[12px]">// insufficient data</div>
        ) : (
          <div className="space-y-2 text-[12px] font-mono">
            {Object.entries(strat).map(([k, v]) => (
              <div key={k} className="flex items-start justify-between gap-3 py-1.5 border-b border-[var(--border-default)]" data-testid={`strategy-${k}`}>
                <div>
                  <span className="text-cyan-sys">{k}</span>
                  <span className="text-[var(--txt-muted)] ml-2 text-[10px]">n={v.sample_size}</span>
                </div>
                <div className="text-right">
                  {v.best_threshold != null ? (
                    <>
                      <div>best ≥ <span className="text-buy">{v.best_threshold}</span> · WR {v.win_rate_at_best}% <span className={v.lift_pts > 0 ? 'text-buy' : 'text-hold'}>({v.lift_pts >= 0 ? '+' : ''}{v.lift_pts})</span></div>
                      <div className="text-[10px] text-[var(--txt-secondary)] italic">{v.recommend}</div>
                    </>
                  ) : <span className="text-[var(--txt-muted)] text-[10px]">{v.recommend}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </CornerPanel>

      {/* P11: Calibration Table */}
      <CornerPanel
        title="Confidence Calibration // predicted vs realized"
        right={calib?.drift_score != null ? (
          <span className="text-[10px] tracking-[0.2em] uppercase text-cyan-sys">
            drift {calib.drift_score} · n={calib.sample_size}
          </span>
        ) : null}
        testId="calibration-panel"
      >
        {!calib || !calib.buckets?.length ? (
          <div className="text-[var(--txt-muted)] text-[12px]">// insufficient trades — need ≥25</div>
        ) : (
          <div className="mobile-scroll" data-sticky-first="true">
            <table className="w-full text-[12px] font-mono min-w-[720px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.25em] text-cyan-sys border-b border-[var(--border-default)]">
                  <th className="text-left py-1.5 px-2">confluence</th>
                  <th className="text-left py-1.5 px-2">n</th>
                  <th className="text-left py-1.5 px-2">predicted</th>
                  <th className="text-left py-1.5 px-2">realized</th>
                  <th className="text-left py-1.5 px-2">delta</th>
                </tr>
              </thead>
              <tbody>
                {calib.buckets.map((b) => (
                  <tr key={b.bucket} className="border-b border-[var(--border-default)]">
                    <td className="py-1.5 px-2">{b.bucket}</td>
                    <td className="py-1.5 px-2">{b.n}</td>
                    <td className="py-1.5 px-2 text-[var(--txt-secondary)]">{b.predicted_wr}%</td>
                    <td className="py-1.5 px-2">{b.realized_wr}%</td>
                    <td className={`py-1.5 px-2 ${b.delta_pts >= 0 ? 'text-buy' : 'text-sell'}`}>
                      {b.delta_pts >= 0 ? '+' : ''}{b.delta_pts}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CornerPanel>
    </div>
  );
}
